<script lang="ts">
    import { createEventDispatcher, onMount, tick } from 'svelte'
    import dayjs from 'dayjs'
    import DocumentHeader from '../shared/document-header.svelte'
    import DocumentFooter from '../shared/document-footer.svelte'
    import { sanitizeHtml } from '../utils/sanitize-html'
    import { getEffectiveBlockWidth } from '../blocks/block-registry'
    import type { PlacedBlock } from '../types/placed-block'
    import type { DocumentHeaderFooter } from '../types/document-header-footer'
    import { getMarkerTools, findMarkerTool } from '../types/marker-types'
    import { findGlogauOption } from '../types/glogau-scale'
    import { findAcneGradeOption } from '../types/acne-grade'
    import { ROSACEA_SUBTYPE_OPTIONS } from '../types/rosacea-grade'
    import { ACNE_SCAR_OPTIONS } from '../types/acne-scar'
    import { findFitzpatrickType } from '../types/fitzpatrick-scale'
    import { findCelluliteGrade } from '../types/cellulite-grade'
    import { findStretchMarksOption } from '../types/stretch-marks'
    import { findHairType } from '../types/hair-type'
    import { findHairStroke } from '../types/hair-stroke'
    import { computeImc, classifyImc } from '../types/imc-calculator'
    import { isSignatureMeta, computeRowNumbers } from '../types/dynamic-table'
    import Odontograma from '../odontograma/Odontograma.svelte'
    import type { FillContext } from './context-snapshot'
    import { formatDateTime } from '../utils/format-datetime'

    const ODONTOGRAMA_STAGES = [
        { label: 'Pendente', color: '#FF9800' },
        { label: 'Concluído', color: '#4CAF50' },
        { label: 'Não tratar', color: '#607D8B' },
    ]

    // `break-inside: avoid` (ver CSS) evita cortar um campo no meio ao virar a página, mas em blocos
    // que podem ser bem altos — um contrato inteiro num "texto fixo", uma tabela com muitas linhas —
    // ele tem o efeito oposto: se o bloco não couber no que resta da página, o navegador empurra o
    // bloco inteiro pra próxima e deixa a anterior quase vazia. Esses tipos podem quebrar à vontade.
    const BREAKABLE_BLOCK_TYPES = ['label', 'text_rich', 'text_long', 'dynamic_table']

    // Fuso vem do contexto congelado, não da máquina: este mesmo componente é renderizado pela
    // lambda-pdf (servidor em UTC) para gerar o documento que segue para assinatura.
    function formatSignedAt(iso: string): string {
        return formatDateTime(iso, context.timezoneOffsetMinutes)
    }

    /**
     * Versão estática (divs, sem inputs/canvas/editores) do preenchimento, usada só para impressão —
     * tanto pelo link interno (via window.printer) quanto pela página pública (alternada via @media print).
     * Dispara `loaded` quando todas as imagens (assinatura, fotos, antes/depois) já carregaram ou falharam,
     * já que essas fotos vêm do S3 e podem demorar — quem chama espera esse evento antes de imprimir.
     * Não recebe/exibe o título do modelo — é uso interno, não deve aparecer para quem recebe o link
     * público nem na impressão.
     */
    export let blocks: PlacedBlock[] = []
    export let headerFooter: DocumentHeaderFooter
    export let answers: Record<string, any> = {}
    export let context: FillContext = {}
    /** Só é necessário em contexto público (sem usuário logado) — repassado ao DocumentHeader para
     * buscar os dados da empresa via API pública quando o cabeçalho usa "Usar os dados da empresa". */
    export let companyId: string | null = null
    /** Só preenchido pela página pública de impressão quando chamada pela lambda-pdf (?pdf=1) — exibe
     * o ID do documento no rodapé só na versão gerada em PDF, não na impressão feita por uma pessoa. */
    export let documentFooterId: string | null = null
    /** Quando o cabeçalho/rodapé já são repetidos em toda página pelo próprio frame de impressão
     * (ver runningHeader/runningFooter em share-page.dto.ts), eles não devem sair também aqui no
     * corpo — senão apareceriam duplicados na primeira página. */
    export let omitHeaderFooter = false

    const dispatch = createEventDispatcher()

    $: resolvedBlocks = blocks.map((block) => {
        if (block.type !== 'label') return block
        const text = window.api.utils.utils.formsReplaceTags(
            block.data?.text ?? '',
            context.person,
            context.invoice || context.bill,
            context.attendant,
            new Date(),
            context.invoice ? 'invoice' : context.bill ? 'bill' : undefined,
            context.user?.userName,
        )
        return { ...block, data: { ...block.data, text } }
    })

    // As fotos do bloco "Foto com marcação" ocupam toda a largura útil, sempre 2 por linha — o
    // tamanho varia com a largura do bloco (1/2/3 colunas), então medimos o grid de cada bloco
    // (bind:clientWidth) em vez de usar um valor fixo, tanto para o CSS quanto para a seta.
    const PHOTO_GAP = 10
    let photoGridWidths: Record<string, number> = {}

    function photoSize(blockId: string) {
        const gridWidth = photoGridWidths[blockId]
        return gridWidth ? (gridWidth - PHOTO_GAP) / 2 : 320
    }

    // Mesma lógica para o bloco "Antes e depois" — as duas fotos (lado a lado) ocupam toda a
    // largura útil do bloco em vez do tamanho fixo antigo.
    const BEFORE_AFTER_GAP = 16
    let beforeAfterWidths: Record<string, number> = {}

    function beforeAfterSize(blockId: string) {
        const containerWidth = beforeAfterWidths[blockId]
        return containerWidth ? (containerWidth - BEFORE_AFTER_GAP) / 2 : 320
    }

    function arrowGeometry(point: { x: number; y: number; x2?: number; y2?: number }, size: number) {
        const x2 = point.x2 ?? point.x
        const y2 = point.y2 ?? point.y
        const dxPx = ((x2 - point.x) / 100) * size
        const dyPx = ((y2 - point.y) / 100) * size
        const length = Math.hypot(dxPx, dyPx)
        const angle = (Math.atan2(dyPx, dxPx) * 180) / Math.PI
        return { length, angle }
    }

    function choiceMultipleText(value: string[]) {
        return Array.isArray(value) && value.length > 0 ? value.join(', ') : '—'
    }

    function dateText(value: string) {
        return value ? dayjs(value).format('DD/MM/YYYY') : '—'
    }

    function fileSize(bytes: number) {
        if (!bytes) return ''
        if (bytes < 1024) return `${bytes} B`
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    }

    let rootEl: HTMLElement
    let expectedImages = 0
    // Conjunto de elementos, não contador: a varredura de imagens já completas (ver
    // settleAlreadyLoaded) e o evento `load` podem alcançar a mesma imagem, e somar duas vezes
    // dispararia o `loaded` antes das outras terminarem.
    const settledImages = new Set<EventTarget>()
    let dispatchedLoaded = false

    function countExpectedImages() {
        let count = 0
        for (const block of blocks) {
            const value = answers[block.id]
            if (block.type === 'sign' && value) count++
            if (block.type === 'image_annotation' && Array.isArray(value)) count += value.length
            if (block.type === 'before_after' && value) {
                if (value.before?.src) count++
                if (value.after?.src) count++
            }
        }
        return count
    }

    function fireLoaded() {
        if (dispatchedLoaded) return
        dispatchedLoaded = true
        dispatch('loaded')
    }

    function onImageSettled(event: Event) {
        if (event.currentTarget) settledImages.add(event.currentTarget)
        if (settledImages.size >= expectedImages) fireLoaded()
    }

    /**
     * Imagem que já estava completa quando o handler foi ligado nunca dispara `load`.
     *
     * Acontece direto aqui: a mesma foto costuma ter sido exibida antes (no preenchimento, numa
     * impressão anterior) e vem do cache já decodificada. O contador nunca fechava, e a impressão
     * só saía pelo timeout de segurança de 8s — em toda geração de PDF, não só num caso de borda.
     *
     * Os seletores são os mesmos elementos que o `countExpectedImages` conta; o logo do cabeçalho
     * fica de fora nos dois lados, senão a varredura fecharia a conta antes da hora.
     */
    function settleAlreadyLoaded() {
        if (!rootEl) return
        // `forEach` e não `for...of`: o alvo de compilação do projeto não itera NodeList direto.
        rootEl.querySelectorAll<HTMLImageElement>('.fv2-print-sign, .fv2-print-photo').forEach((img) => {
            // `complete` também é true para imagem que falhou — e falha conta como resolvida, igual
            // ao `on:error` que já alimenta o mesmo conjunto.
            if (img.complete) settledImages.add(img)
        })
        if (settledImages.size >= expectedImages) fireLoaded()
    }

    onMount(async () => {
        expectedImages = countExpectedImages()
        await tick()
        if (expectedImages === 0) fireLoaded()
        settleAlreadyLoaded()
        // Rede lenta ou imagem que nunca resolve não deve travar a impressão para sempre.
        setTimeout(fireLoaded, 8000)
    })
</script>

<div class="fv2-print" bind:this={rootEl}>
    {#if !omitHeaderFooter}
        <DocumentHeader header={headerFooter?.header} {companyId} />
    {/if}

    <div class="fv2-print-grid">
    {#each resolvedBlocks as block (block.id)}
        {@const value = answers[block.id]}
        {@const blockWidth = getEffectiveBlockWidth(block)}
        <div
            class="fv2-print-block"
            class:can-break={BREAKABLE_BLOCK_TYPES.includes(block.type)}
            class:w1={blockWidth === 1}
            class:w2={blockWidth === 2}
            class:w3={blockWidth === 3}
            style="grid-column: span {blockWidth}"
        >
            {#if block.type === 'label'}
                <div class="fv2-print-html">{@html sanitizeHtml(block.data?.text)}</div>
            {:else if block.type === 'text_short' || block.type === 'text_long'}
                <div class="fv2-print-label">{block.label}</div>
                <div class="fv2-print-value">{value || '—'}</div>
            {:else if block.type === 'text_rich'}
                <div class="fv2-print-label">{block.label}</div>
                {#if value && String(value).replace(/<[^>]*>/g, '').trim() !== ''}
                    <div class="fv2-print-html">{@html sanitizeHtml(value)}</div>
                {:else}
                    <div class="fv2-print-value">—</div>
                {/if}
            {:else if block.type === 'date'}
                <div class="fv2-print-label">{block.label}</div>
                <div class="fv2-print-value">{dateText(value)}</div>
            {:else if block.type === 'choice_one'}
                <div class="fv2-print-label">{block.label}</div>
                <div class="fv2-print-value">{value || '—'}</div>
            {:else if block.type === 'choice_multiple'}
                <div class="fv2-print-label">{block.label}</div>
                <div class="fv2-print-value">{choiceMultipleText(value)}</div>
            {:else if block.type === 'sign'}
                <div class="fv2-print-label">{block.label}</div>
                {#if value}
                    <img class="fv2-print-sign" src={value} alt={block.label} on:load={onImageSettled} on:error={onImageSettled} />
                {:else}
                    <div class="fv2-print-value">Não assinado</div>
                {/if}
            {:else if block.type === 'image_annotation'}
                <div class="fv2-print-label">{block.label}</div>
                {#if Array.isArray(value) && value.length > 0}
                    {@const size = photoSize(block.id)}
                    {@const tools = getMarkerTools(block.data)}
                    <div class="fv2-print-photo-grid" bind:clientWidth={photoGridWidths[block.id]}>
                        {#each value as item (item.id)}
                            <div class="fv2-print-photo-wrap" style="width:{size}px; height:{size}px;">
                                <img class="fv2-print-photo" src={item.dataUrl} alt={item.name} on:load={onImageSettled} on:error={onImageSettled} />
                                {#each item.points ?? [] as point (point.id)}
                                    {@const marker = findMarkerTool(tools, point.type)}
                                    {#if marker.shape === 'arrow'}
                                        {@const geo = arrowGeometry(point, size)}
                                        <div
                                            class="fv2-print-arrow"
                                            style="top:{point.y}%; left:{point.x}%; width:{geo.length}px; transform: translateY(-50%) rotate({geo.angle}deg); background:{marker.color};"
                                        >
                                            <span class="fv2-print-arrow-head" style="border-left-color:{marker.color};" />
                                        </div>
                                        <div class="fv2-print-point" style="top:{point.y2 ?? point.y}%; left:{point.x2 ?? point.x}%;">
                                            <span class="fv2-print-point-label">{point.value} {marker.unit}</span>
                                        </div>
                                    {:else}
                                        <div class="fv2-print-point" style="top:{point.y}%; left:{point.x}%;">
                                            <span class="fv2-print-point-dot" style="background:{marker.color}" />
                                            <span class="fv2-print-point-label">{point.value} {marker.unit}</span>
                                        </div>
                                    {/if}
                                {/each}
                            </div>
                        {/each}
                    </div>
                {:else}
                    <div class="fv2-print-value">—</div>
                {/if}
            {:else if block.type === 'before_after'}
                <div class="fv2-print-label">{block.label}</div>
                {#if value?.before?.src || value?.after?.src}
                    {@const size = beforeAfterSize(block.id)}
                    <div class="fv2-print-before-after" bind:clientWidth={beforeAfterWidths[block.id]}>
                        <figure>
                            {#if value.before?.src}
                                <img
                                    class="fv2-print-photo"
                                    src={value.before.src}
                                    alt="Antes"
                                    style="width:{size}px; height:{size}px;"
                                    on:load={onImageSettled}
                                    on:error={onImageSettled}
                                />
                            {/if}
                            <figcaption>Antes</figcaption>
                        </figure>
                        <figure>
                            {#if value.after?.src}
                                <img
                                    class="fv2-print-photo"
                                    src={value.after.src}
                                    alt="Depois"
                                    style="width:{size}px; height:{size}px;"
                                    on:load={onImageSettled}
                                    on:error={onImageSettled}
                                />
                            {/if}
                            <figcaption>Depois</figcaption>
                        </figure>
                    </div>
                {:else}
                    <div class="fv2-print-value">—</div>
                {/if}
            {:else if block.type === 'file_upload'}
                <div class="fv2-print-label">{block.label}</div>
                {#if Array.isArray(value) && value.length > 0}
                    <ul class="fv2-print-file-list">
                        {#each value as file (file.id)}
                            <li>{file.name} <span class="fv2-print-file-size">({fileSize(file.size)})</span></li>
                        {/each}
                    </ul>
                {:else}
                    <div class="fv2-print-value">—</div>
                {/if}
            {:else if block.type === 'glogau_scale'}
                {@const opt = findGlogauOption(value)}
                <div class="fv2-print-label">{block.label}</div>
                <div class="fv2-print-value">{opt ? `${opt.label} — ${opt.description}` : '—'}</div>
            {:else if block.type === 'acne_grade'}
                {@const opt = findAcneGradeOption(value)}
                <div class="fv2-print-label">{block.label}</div>
                <div class="fv2-print-value">{opt ? `${opt.label} — ${opt.subtype}` : '—'}</div>
            {:else if block.type === 'rosacea_grade'}
                <div class="fv2-print-label">{block.label}</div>
                {#if value && typeof value === 'object'}
                    <table class="fv2-print-scale-table">
                        {#each ROSACEA_SUBTYPE_OPTIONS as subtype}
                            <tr>
                                <td class="fv2-print-scale-name">{subtype.name}</td>
                                <td class="fv2-print-scale-val">{value[subtype.key] ?? '—'}</td>
                            </tr>
                        {/each}
                    </table>
                {:else}
                    <div class="fv2-print-value">—</div>
                {/if}
            {:else if block.type === 'acne_scar'}
                <div class="fv2-print-label">{block.label}</div>
                <div class="fv2-print-value">
                    {Array.isArray(value) && value.length > 0
                        ? ACNE_SCAR_OPTIONS.filter((o) => value.includes(o.key)).map((o) => o.label).join(', ')
                        : '—'}
                </div>
            {:else if block.type === 'fitzpatrick_scale'}
                {@const opt = findFitzpatrickType(value)}
                <div class="fv2-print-label">{block.label}</div>
                <div class="fv2-print-value">{opt ? `${opt.label} — ${opt.description}` : '—'}</div>
            {:else if block.type === 'cellulite_grade'}
                {@const opt = findCelluliteGrade(value)}
                <div class="fv2-print-label">{block.label}</div>
                <div class="fv2-print-value">{opt ? `${opt.label} — ${opt.description}` : '—'}</div>
            {:else if block.type === 'stretch_marks'}
                {@const opt = findStretchMarksOption(value)}
                <div class="fv2-print-label">{block.label}</div>
                <div class="fv2-print-value">{opt ? `${opt.label} — ${opt.description}` : '—'}</div>
            {:else if block.type === 'hair_type'}
                {@const opt = findHairType(value)}
                <div class="fv2-print-label">{block.label}</div>
                <div class="fv2-print-value">{opt ? `${opt.label} — ${opt.description}` : '—'}</div>
            {:else if block.type === 'hair_stroke'}
                {@const opt = findHairStroke(value)}
                <div class="fv2-print-label">{block.label}</div>
                <div class="fv2-print-value">{opt ? `${opt.label} — ${opt.description}` : '—'}</div>
            {:else if block.type === 'imc_calculator'}
                {@const imc = computeImc(value?.pesoKg ?? null, value?.alturaCm ?? null)}
                {@const cls = imc !== null ? classifyImc(imc) : null}
                <div class="fv2-print-label">{block.label}</div>
                <table class="fv2-print-scale-table">
                    <tr><td class="fv2-print-scale-name">Peso</td><td class="fv2-print-scale-val">{value?.pesoKg != null ? `${value.pesoKg} kg` : '—'}</td></tr>
                    <tr><td class="fv2-print-scale-name">Altura</td><td class="fv2-print-scale-val">{value?.alturaCm != null ? `${value.alturaCm} cm` : '—'}</td></tr>
                    <tr><td class="fv2-print-scale-name">IMC</td><td class="fv2-print-scale-val">{imc !== null ? imc.toFixed(1) : '—'}</td></tr>
                    {#if cls}<tr><td class="fv2-print-scale-name">Classificação</td><td class="fv2-print-scale-val">{cls.tipo}</td></tr>{/if}
                </table>
            {:else if block.type === 'dynamic_table'}
                {@const rows = value?.rows ?? []}
                {@const cols = block.data?.columns ?? []}
                {@const rowNums = computeRowNumbers(rows)}
                <div class="fv2-print-label">{block.label}</div>
                {#if cols.length > 0 && rows.length > 0}
                    <div class="fv2-print-table-wrap">
                        <table class="fv2-print-dyntable">
                            <thead>
                                <tr>{#each cols as col}<th>{col.label}</th>{/each}</tr>
                            </thead>
                            <tbody>
                                {#each rows as row, i}
                                    <tr>
                                        {#each cols as col}
                                            <td>
                                                {#if col.type === 'row_number'}
                                                    {rowNums[i] ?? ''}
                                                {:else if col.type === 'signature'}
                                                    {#if isSignatureMeta(row.cells[col.id])}
                                                        Assinado em {formatSignedAt(row.cells[col.id].signedAt)}
                                                    {:else}
                                                        Pendente
                                                    {/if}
                                                {:else if col.type === 'date' && row.cells[col.id]}
                                                    {String(row.cells[col.id]).split('-').reverse().join('/')}
                                                {:else}
                                                    {row.cells[col.id] ?? '—'}
                                                {/if}
                                            </td>
                                        {/each}
                                    </tr>
                                {/each}
                            </tbody>
                        </table>
                    </div>
                {:else}
                    <div class="fv2-print-value">—</div>
                {/if}
            {:else if block.type === 'odontograma'}
                <div class="fv2-print-label">{block.label}</div>
                <div class="fv2-print-odontograma">
                    <Odontograma stages={ODONTOGRAMA_STAGES} value={value ?? {}} readOnly={true} />
                </div>
            {/if}
        </div>
    {/each}
    </div>

    {#if !omitHeaderFooter}
        <DocumentFooter footer={headerFooter?.footer} />
    {/if}
    {#if documentFooterId}
        <div class="fv2-print-doc-id">ID: {documentFooterId}</div>
    {/if}
</div>

<style>
    .fv2-print {
        max-width: 900px;
        margin: 0 auto;
        background: #fff;
        color: #212529;
    }
    .fv2-print-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 14px;
    }
    .fv2-print-block {
        min-width: 0;
        break-inside: avoid;
    }
    .fv2-print-block.can-break {
        break-inside: auto;
    }

    @media print {
        /* Fora da grade na impressão. O Chrome fragmenta mal um container `grid` entre páginas: o
           item alto (um contrato inteiro num "texto fixo") é posicionado pela grade e ignora o
           espaço que o cabeçalho repetido reserva no topo de cada página, ficando por baixo dele.
           Em fluxo normal a quebra respeita esse espaço. */
        .fv2-print-grid {
            display: block;
            /* Zera o espaço em branco entre os blocos estreitos, que viram inline-block abaixo.
               Os filhos dimensionam em `rem`, então não são afetados por herdarem font-size 0. */
            font-size: 0;
        }
        .fv2-print-block {
            box-sizing: border-box;
            margin-bottom: 14px;
            font-size: 1rem;
        }
        /* Largura cheia precisa ser bloco de fluxo normal pra poder quebrar entre páginas — um
           inline-block é caixa atômica e não fragmenta, justamente o caso do contrato longo. */
        .fv2-print-block.w3 {
            display: block;
            width: 100%;
        }
        /* Estreitos seguem lado a lado: são campos curtos, então não fragmentar não é problema
           (eles já tinham `break-inside: avoid` por padrão). */
        .fv2-print-block.w1,
        .fv2-print-block.w2 {
            display: inline-block;
            vertical-align: top;
            padding-right: 14px;
        }
        .fv2-print-block.w1 {
            width: 33.333%;
        }
        .fv2-print-block.w2 {
            width: 66.666%;
        }

        /* O css_print.css aplica `overflow-y: hidden` em `body *`. Quando um eixo é `visible` e o
           outro não, o `visible` passa a computar como `auto` — ou seja, todo elemento vira um
           scroll container, e scroll container é monolítico: não pode ser dividido entre páginas.
           Restauramos `overflow: visible` só na cadeia que precisa paginar (estrutura, texto longo
           e tabelas), deixando de fora foto/odontograma, que dependem do recorte pra não vazar. */
        .fv2-print,
        .fv2-print-grid,
        .fv2-print-block,
        .fv2-print-html,
        .fv2-print-html :global(*),
        .fv2-print-value,
        .fv2-print-table-wrap,
        .fv2-print-dyntable,
        .fv2-print-dyntable tbody,
        .fv2-print-dyntable tr,
        .fv2-print-dyntable td {
            overflow: visible !important;
        }
    }
    .fv2-print-html {
        font-size: 0.92rem;
        line-height: 1.5;
    }
    /* Imagem colada pelo editor não casa com nenhuma classe daqui (.fv2-print-sign, .fv2-print-photo)
       e ia para o layout no tamanho natural — 15000px de largura numa folha A4. O timbre desta
       página é repintado a cada página (ver form-v2-print-frame.svelte), então imagem sem limite
       multiplica o custo de rasterização. `:global` porque conteúdo de {@html} não recebe a classe
       de escopo do Svelte. */
    .fv2-print-html :global(img) {
        max-width: 100%;
        height: auto;
    }
    .fv2-print-label {
        font-size: 0.76rem;
        font-weight: 600;
        color: #495057;
        text-transform: uppercase;
        letter-spacing: 0.03em;
        margin-bottom: 3px;
    }
    .fv2-print-value {
        font-size: 0.92rem;
        color: #212529;
        border-bottom: 1px solid #dee2e6;
        padding-bottom: 4px;
        white-space: pre-wrap;
    }
    .fv2-print-sign {
        display: block;
        width: auto;
        max-width: 100%;
        height: auto;
        max-height: 140px;
        border-bottom: 1px solid #dee2e6;
        padding-bottom: 6px;
    }
    .fv2-print-photo-grid {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
    }
    .fv2-print-photo {
        width: 160px;
        height: 160px;
        object-fit: cover;
        border-radius: 6px;
        border: 1px solid #dee2e6;
    }
    .fv2-print-photo-wrap {
        position: relative;
        flex-shrink: 0;
    }
    .fv2-print-photo-wrap .fv2-print-photo {
        width: 100%;
        height: 100%;
    }
    .fv2-print-point {
        position: absolute;
        transform: translate(-50%, -50%);
        display: flex;
        align-items: center;
        gap: 3px;
    }
    .fv2-print-point-dot {
        flex-shrink: 0;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        border: 1.5px solid #fff;
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.35);
    }
    .fv2-print-point-label {
        font-size: 0.62rem;
        color: #fff;
        background: rgba(17, 19, 24, 0.85);
        padding: 1px 4px;
        border-radius: 3px;
        white-space: nowrap;
    }
    .fv2-print-arrow {
        position: absolute;
        height: 3px;
        border-radius: 2px;
        transform-origin: 0 50%;
    }
    .fv2-print-arrow-head {
        position: absolute;
        right: -1px;
        top: 50%;
        transform: translateY(-50%);
        width: 0;
        height: 0;
        border-style: solid;
        border-width: 5px 0 5px 8px;
        border-color: transparent;
    }
    .fv2-print-before-after {
        display: flex;
        gap: 16px;
    }
    .fv2-print-before-after figure {
        margin: 0;
        text-align: center;
    }
    .fv2-print-before-after figcaption {
        font-size: 0.76rem;
        color: #868e96;
        margin-top: 4px;
    }
    .fv2-print-file-list {
        margin: 0;
        padding-left: 18px;
        font-size: 0.9rem;
    }
    .fv2-print-file-size {
        color: #868e96;
        font-size: 0.8rem;
    }
    .fv2-print-doc-id {
        margin-top: 6px;
        text-align: center;
        font-size: 0.68rem;
        color: #adb5bd;
    }
    .fv2-print-scale-table {
        border-collapse: collapse;
        width: 100%;
        font-size: 0.88rem;
    }
    .fv2-print-scale-table td {
        padding: 2px 6px 2px 0;
        vertical-align: top;
    }
    .fv2-print-scale-name {
        color: #868e96;
        white-space: nowrap;
        padding-right: 12px !important;
        font-size: 0.8rem;
    }
    .fv2-print-scale-val {
        color: #212529;
        font-weight: 500;
    }
    .fv2-print-table-wrap {
        overflow-x: auto;
    }
    .fv2-print-dyntable {
        width: 100%;
        border-collapse: collapse;
        font-size: 0.82rem;
    }
    .fv2-print-dyntable th,
    .fv2-print-dyntable td {
        border: 1px solid #dee2e6;
        padding: 5px 8px;
        text-align: left;
        vertical-align: middle;
    }
    .fv2-print-dyntable th {
        background: #f8f9fa;
        font-weight: 600;
        font-size: 0.76rem;
        color: #495057;
        white-space: nowrap;
    }
    .fv2-print-odontograma {
        margin-top: 4px;
        break-inside: avoid;
    }
</style>
